import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Folder, FileCode, ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';

interface FileNode {
  name: string;
  path: string;
  type: 'file' | 'folder';
  children?: FileNode[];
}

export function DeveloperDashboard() {
  const navigate = useNavigate();
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['/src']));

  const projectStructure: FileNode[] = [
    {
      name: 'src',
      path: '/src',
      type: 'folder',
      children: [
        {
          name: 'pages',
          path: '/src/pages',
          type: 'folder',
          children: [
            { name: 'HomePage', path: '/', type: 'file' },
            { name: 'AuthPage', path: '/auth', type: 'file' },
            { name: 'OnboardingPage', path: '/onboarding', type: 'file' },
            { name: 'MatchesPage', path: '/matches', type: 'file' },
            { name: 'ProfilePage', path: '/profile', type: 'file' },
            { name: 'TesterGuide', path: '/guide', type: 'file' },
            { name: 'AdminDashboard', path: '/admin', type: 'file' },
            { name: 'ListingsPage', path: '/listings', type: 'file' },
            { name: 'LoneStayPage', path: '/lonestay', type: 'file' },
            { name: 'DeveloperDashboard', path: '/dev', type: 'file' }
          ]
        },
        {
          name: 'components',
          path: '/src/components',
          type: 'folder',
          children: [
            { name: 'Navigation', path: '/components/navigation', type: 'file' },
            { name: 'ActiveStatusCheck', path: '/components/status', type: 'file' },
            { name: 'ApplicationStatus', path: '/components/application', type: 'file' },
            { name: 'ConnectionStatus', path: '/components/connection', type: 'file' },
            { name: 'FeedbackForm', path: '/components/feedback', type: 'file' },
            { name: 'ListingsMap', path: '/components/map', type: 'file' },
            { name: 'OpenAITest', path: '/components/openai', type: 'file' },
            { name: 'PaymentForm', path: '/components/payment', type: 'file' },
            { name: 'SearchBox', path: '/components/search', type: 'file' },
            { name: 'TwoFactorSetup', path: '/components/2fa', type: 'file' }
          ]
        },
        {
          name: 'lib',
          path: '/src/lib',
          type: 'folder',
          children: [
            { name: 'api.ts', path: '/lib/api', type: 'file' },
            { name: 'auth.ts', path: '/lib/auth', type: 'file' },
            { name: 'hooks.ts', path: '/lib/hooks', type: 'file' },
            { name: 'supabase.ts', path: '/lib/supabase', type: 'file' },
            { name: 'types.ts', path: '/lib/types', type: 'file' }
          ]
        }
      ]
    },
    {
      name: 'supabase',
      path: '/supabase',
      type: 'folder',
      children: [
        {
          name: 'functions',
          path: '/supabase/functions',
          type: 'folder',
          children: [
            { name: 'analyze-preferences', path: '/functions/analyze', type: 'file' },
            { name: 'create-payment-intent', path: '/functions/payment', type: 'file' },
            { name: 'generate-totp', path: '/functions/totp', type: 'file' },
            { name: 'neighborhoods', path: '/functions/neighborhoods', type: 'file' },
            { name: 'notify-feedback', path: '/functions/notify', type: 'file' },
            { name: 'search', path: '/functions/search', type: 'file' },
            { name: 'setup-email-2fa', path: '/functions/2fa', type: 'file' },
            { name: 'sync-listings', path: '/functions/sync', type: 'file' },
            { name: 'verify-openai', path: '/functions/verify', type: 'file' },
            { name: 'verify-totp', path: '/functions/verify-totp', type: 'file' }
          ]
        },
        {
          name: 'migrations',
          path: '/supabase/migrations',
          type: 'folder',
          children: [
            { name: 'User Preferences', path: '/migrations/preferences', type: 'file' },
            { name: 'Listings & Transit', path: '/migrations/listings', type: 'file' },
            { name: 'Profiles & Auth', path: '/migrations/profiles', type: 'file' },
            { name: 'Feedback System', path: '/migrations/feedback', type: 'file' },
            { name: 'Payment System', path: '/migrations/payments', type: 'file' },
            { name: 'Search History', path: '/migrations/search', type: 'file' },
            { name: 'Shared Plans', path: '/migrations/plans', type: 'file' },
            { name: 'Events System', path: '/migrations/events', type: 'file' }
          ]
        }
      ]
    }
  ];

  const toggleFolder = (path: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const renderNode = (node: FileNode, depth: number = 0) => {
    const isExpanded = expandedFolders.has(node.path);
    const paddingLeft = `${depth * 20}px`;

    if (node.type === 'folder') {
      return (
        <div key={node.path}>
          <button
            onClick={() => toggleFolder(node.path)}
            className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-100 transition-colors"
            style={{ paddingLeft }}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500" />
            )}
            <Folder className="w-4 h-4 text-blue-500" />
            <span className="font-medium">{node.name}</span>
          </button>
          {isExpanded && node.children?.map(child => renderNode(child, depth + 1))}
        </div>
      );
    }

    return (
      <button
        key={node.path}
        onClick={() => navigate(node.path)}
        className="w-full flex items-center gap-2 px-4 py-2 hover:bg-gray-100 transition-colors"
        style={{ paddingLeft }}
      >
        <FileCode className="w-4 h-4 text-gray-500" />
        <span>{node.name}</span>
        <ExternalLink className="w-3 h-3 text-gray-400 ml-auto" />
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="border-b border-gray-200 px-6 py-4">
            <h1 className="text-xl font-semibold text-gray-900">Developer Dashboard</h1>
            <p className="text-sm text-gray-500">Browse project structure and navigate to pages</p>
          </div>
          <div className="divide-y divide-gray-100">
            {projectStructure.map(node => renderNode(node))}
          </div>
        </div>
      </div>
    </div>
  );
}